import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid
} from 'recharts';
import { getEarningsByStream, type Stream } from '@/api/fleet';
import { STREAM_META, formatUsd, formatRev } from '@/lib/format';

type Range = { days: number; label: string };
const RANGES: Range[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' }
];

const STREAM_ORDER: Stream[] = [
  'cpu_compute',
  'gpu_compute',
  'storage',
  'bandwidth',
  'mixed'
];

const STREAM_HEX: Record<Stream, string> = {
  cpu_compute: '#a78bfa',
  gpu_compute: '#34d399',
  storage: '#fbbf24',
  bandwidth: '#60a5fa',
  mixed: '#9ca3af'
};

export function EarningsScreen() {
  const [range, setRange] = useState(RANGES[0]);
  const { data, isLoading } = useQuery({
    queryKey: ['fleet-earnings', range.days],
    queryFn: () => getEarningsByStream(range.days)
  });

  // Reshape byDayStream into one row per date with one column per stream
  const chartData = useMemo(() => {
    if (!data?.byDayStream) return [];
    const byDate = new Map<string, Record<string, number | string>>();
    for (const r of data.byDayStream) {
      const row = byDate.get(r.dateKey) || { date: r.dateKey };
      row[r.stream] = ((row[r.stream] as number) || 0) + r.usdAmount;
      byDate.set(r.dateKey, row);
    }
    return Array.from(byDate.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
  }, [data]);

  const totals = data?.totals;
  const byStream = data?.byStream ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-bold text-nurev-text">Earnings</h1>
          <p className="mt-1 text-sm text-nurev-muted">
            Per-stream revenue, sourced from `FleetEarning` daily aggregates.
            Numbers reflect what your nodes actually earned upstream.
          </p>
        </div>
        <div className="flex gap-1.5 rounded-lg bg-white/[0.04] p-1">
          {RANGES.map(r => (
            <button
              key={r.days}
              onClick={() => setRange(r)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                range.days === r.days
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'text-nurev-muted hover:text-nurev-text'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-3 text-nurev-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading earnings…
        </div>
      ) : (
        <>
          {/* Total card */}
          <div className="card flex items-end justify-between">
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-nurev-muted">
                Total in window
              </div>
              <div className="mt-1 text-3xl font-bold text-emerald-300">
                {formatUsd(totals?.usdAmount)}
              </div>
              <div className="mt-1 text-xs text-nurev-muted">
                {formatRev(totals?.revAmount)} REV ·{' '}
                {totals?.streamsWithActivity || 0} stream
                {totals?.streamsWithActivity === 1 ? '' : 's'} with activity
              </div>
            </div>
          </div>

          {/* Stacked daily chart */}
          <div className="card">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-emerald-300">
              Daily USD by stream
            </h2>
            {chartData.length === 0 ? (
              <p className="py-12 text-center text-sm text-nurev-muted">
                No daily activity in the selected window.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1d2e" />
                  <XAxis
                    dataKey="date"
                    stroke="#7b7ea8"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    stroke="#7b7ea8"
                    tick={{ fontSize: 11 }}
                    tickFormatter={v => `$${v.toFixed(0)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#0e0f1a',
                      border: '1px solid #1a1d2e',
                      borderRadius: 8
                    }}
                    formatter={(value: number, name: string) => [
                      formatUsd(value),
                      STREAM_META[name as Stream]?.label || name
                    ]}
                  />
                  {STREAM_ORDER.map(s => (
                    <Area
                      key={s}
                      type="monotone"
                      dataKey={s}
                      stackId="1"
                      stroke={STREAM_HEX[s]}
                      fill={STREAM_HEX[s]}
                      fillOpacity={0.25}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Per-stream breakdown table */}
          <div className="card overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-nurev-border bg-white/[0.02] text-left text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
                  <th className="px-4 py-3">Stream</th>
                  <th className="px-4 py-3 text-right">USD</th>
                  <th className="px-4 py-3 text-right">REV</th>
                  <th className="px-4 py-3 text-right">Blocks</th>
                  <th className="px-4 py-3 text-right">Nodes</th>
                </tr>
              </thead>
              <tbody>
                {byStream.map(s => {
                  const meta = STREAM_META[s.stream];
                  return (
                    <tr
                      key={s.stream}
                      className="border-b border-nurev-border/60"
                    >
                      <td className={`px-4 py-3 font-medium ${meta.color}`}>
                        {meta.emoji} {meta.label}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-nurev-text">
                        {formatUsd(s.usdAmount)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-nurev-muted">
                        {formatRev(s.revAmount)}
                      </td>
                      <td className="px-4 py-3 text-right text-nurev-muted">
                        {s.blockCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right text-nurev-muted">
                        {s.distinctNodes}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
